import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { Select } from '@/components/Select'
import { Dialog, Field, GhostButton, INPUT, PrimaryButton } from '@/features/inventory/shared'
import { STATE_BG, stateLabel, useExecT } from '@/features/execution/shared'
import type { State } from '@/features/execution/types'
import { fmtDate } from '@/i18n'
import { PROJECT_ROLES, type ItemView, type Person, type ProjectWorkflow, type Role } from './types'
import { gateLabel, itemLabel, roleLabel, useWfT, type WfT } from './shared'
import { ProjectCrewCompliance } from '@/features/people/PeopleWidgets'

const todayIso = () => new Date().toISOString().slice(0, 10)

/**
 * The project's workflow (docs/project-workflow-design.md): where it is on the route, the next
 * gate with its checklist, and the team. Items the app checks from data tick themselves; the rest
 * are confirmed by their owner. The gate's signer records the next state.
 */
export function WorkflowPanel({ projectId }: { projectId: string }) {
  const t = useWfT()
  const te = useExecT()
  const { data } = useQuery<ProjectWorkflow>({
    queryKey: ['workflow', projectId], queryFn: () => api.get<ProjectWorkflow>(`/api/projects/${projectId}/workflow`),
  })
  const [dialog, setDialog] = useState<{ kind: 'tick'; item: ItemView } | { kind: 'sign' } | { kind: 'other' } | { kind: 'member'; role: Role } | null>(null)
  if (!data) return null
  const g = data.nextGate
  const others = data.me.canRecord.filter((s) => s !== g?.to)

  return (
    <section className="space-y-5 rounded-2xl bg-white p-4 ring-1 ring-neutral-200 sm:p-5 dark:bg-neutral-900 dark:ring-neutral-800">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{t('panelTitle')}</h2>
        {data.me.projectRoles.length > 0 && <span className="text-xs text-neutral-500">{t('myRoles', { roles: data.me.projectRoles.map((r) => roleLabel(t, r)).join(', ') })}</span>}
      </div>

      <Steps steps={data.steps} />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <div className="space-y-3">
          {g ? (
            <>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold">{t('nextGate', { gate: gateLabel(t, g.to) })}</h3>
                  <p className="text-sm text-neutral-500">{t('signedBy', { role: roleLabel(t, g.signer) })} · {g.open === 0 ? t('allDone') : t('openOf', { open: g.open, total: g.items.length })}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {g.canSign && <PrimaryButton onClick={() => setDialog({ kind: 'sign' })}>{t('sign')}</PrimaryButton>}
                  {others.length > 0 && <GhostButton onClick={() => setDialog({ kind: 'other' })}>{t('recordOther')}</GhostButton>}
                </div>
              </div>
              <ul className="divide-y divide-neutral-100 overflow-hidden rounded-xl ring-1 ring-neutral-200 dark:divide-neutral-800 dark:ring-neutral-800">
                {g.items.map((i) => <ItemRow key={i.key} t={t} gate={g.to} item={i} projectId={projectId} onConfirm={() => setDialog({ kind: 'tick', item: i })} />)}
              </ul>
              {!g.canSign && <p className="text-xs text-neutral-500">{t('cantSign', { role: roleLabel(t, g.signer) })}</p>}
              <ProjectCrewCompliance projectId={projectId} />
            </>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-neutral-500">{t('noGate')}</p>
              {others.length > 0 && <GhostButton onClick={() => setDialog({ kind: 'other' })}>{t('recordOther')}</GhostButton>}
            </div>
          )}

          {data.signed.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-neutral-500">{t('history')} ({data.signed.length})</summary>
              <ul className="mt-2 space-y-1.5">
                {data.signed.map((s) => (
                  <li key={s.id} className="flex flex-wrap items-baseline gap-x-2">
                    <span className="tabular text-xs text-neutral-500">{fmtDate(s.at)}</span>
                    <b>{stateLabel(te, s.state)}</b>
                    <span className="text-neutral-600 dark:text-neutral-300">{t('signedAs', { by: s.byName ?? '—', role: s.signedRole ? (s.signedRole === 'admin' ? t('role_admin') : roleLabel(t, s.signedRole)) : '—' })}</span>
                    {s.note && <span className="text-neutral-500">· {s.note}</span>}
                    {s.overrideReason && <span className="text-amber-700 dark:text-amber-300">· {t('wentAhead', { reason: s.overrideReason })}</span>}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        <Team t={t} data={data} onAdd={(role) => setDialog({ kind: 'member', role })} />
      </div>

      {dialog?.kind === 'tick' && g && <TickDialog t={t} projectId={projectId} gate={g.to} item={dialog.item} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'sign' && g && <SignDialog t={t} projectId={projectId} state={g.to} open={g.items.filter((i) => !i.done)} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'other' && <SignDialog t={t} projectId={projectId} choices={others} open={[]} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'member' && <MemberDialog t={t} projectId={projectId} role={dialog.role} taken={data.members.filter((m) => m.role === dialog.role).map((m) => m.userId)} onClose={() => setDialog(null)} />}
    </section>
  )
}

/** The route as steps: done in the state's colour, the current one ringed, the next gate outlined. */
function Steps({ steps }: { steps: ProjectWorkflow['steps'] }) {
  const te = useExecT()
  return (
    <ol className="grid grid-cols-4 gap-1.5 sm:grid-cols-8">
      {steps.map((s) => (
        <li key={s.state} className="min-w-0 space-y-1">
          <div className={`h-2 rounded-full ${s.status === 'done' || s.status === 'current' ? STATE_BG[s.state] : s.status === 'next' ? 'bg-neutral-300 dark:bg-neutral-600' : 'bg-neutral-100 dark:bg-neutral-800'} ${s.status === 'current' ? 'ring-2 ring-offset-2 ring-neutral-900 dark:ring-white dark:ring-offset-neutral-900' : ''}`} />
          <div className={`truncate text-[11px] ${s.status === 'current' ? 'font-semibold' : s.status === 'later' || s.status === 'skipped' ? 'text-neutral-400' : 'text-neutral-600 dark:text-neutral-300'}`} title={stateLabel(te, s.state)}>
            {stateLabel(te, s.state)}
          </div>
          {s.since && <div className="tabular text-[10px] text-neutral-400">{fmtDate(s.since)}</div>}
        </li>
      ))}
    </ol>
  )
}

function ItemRow({ t, gate, item, projectId, onConfirm }: { t: WfT; gate: State; item: ItemView; projectId: string; onConfirm: () => void }) {
  const qc = useQueryClient()
  const undo = useMutation({
    mutationFn: () => api.del(`/api/projects/${projectId}/gates/${gate}/items/${item.key}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['workflow', projectId] }); void qc.invalidateQueries({ queryKey: ['workflow-inbox'] }) },
  })
  return (
    <li className="flex items-start gap-3 bg-white px-3 py-2.5 dark:bg-neutral-900">
      <span aria-hidden className={`mt-0.5 grid size-5 shrink-0 place-items-center rounded-full text-[11px] font-bold ${item.done ? 'bg-green-600 text-white' : 'ring-2 ring-neutral-300 dark:ring-neutral-600'}`}>{item.done ? '✓' : ''}</span>
      <div className="min-w-0 flex-1">
        <div className={`text-sm ${item.done ? '' : 'font-medium'}`}>{itemLabel(t, gate, item.key)}</div>
        <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-neutral-500">
          <span>{t('owner')}: {roleLabel(t, item.owner)}</span>
          {item.source === 'auto' && <span className="rounded bg-neutral-100 px-1.5 dark:bg-neutral-800">{t('fromData')}</span>}
          {item.done && item.byName && <span>{item.byName}{item.at ? `, ${fmtDate(item.at.slice(0, 10))}` : ''}</span>}
          {item.note && <span className="text-neutral-700 dark:text-neutral-300">· {item.note}</span>}
        </div>
      </div>
      {item.canTick && (item.done
        ? <button type="button" onClick={() => undo.mutate()} disabled={undo.isPending} className="min-h-9 shrink-0 rounded-lg px-2 text-xs text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800">{t('undo')}</button>
        : <button type="button" onClick={onConfirm} className="min-h-9 shrink-0 rounded-lg px-3 text-xs font-semibold text-brand-700 ring-1 ring-brand-600/40 hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-brand-500/10">{t('confirm')}</button>)}
    </li>
  )
}

function Team({ t, data, onAdd }: { t: WfT; data: ProjectWorkflow; onAdd: (role: Role) => void }) {
  const qc = useQueryClient()
  const remove = useMutation({
    mutationFn: ({ role, userId }: { role: Role; userId: string }) => api.del(`/api/projects/${data.projectId}/members/${role}/${userId}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['workflow', data.projectId] }); void qc.invalidateQueries({ queryKey: ['workflow-inbox'] }) },
  })
  return (
    <div className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{t('team')}</h3>
      <dl className="divide-y divide-neutral-100 rounded-xl ring-1 ring-neutral-200 dark:divide-neutral-800 dark:ring-neutral-800">
        {PROJECT_ROLES.map((role) => {
          const people = data.members.filter((m) => m.role === role)
          return (
            <div key={role} className="flex items-start justify-between gap-2 px-3 py-2">
              <dt className="pt-1 text-xs text-neutral-500">{roleLabel(t, role)}</dt>
              <dd className="flex min-w-0 flex-wrap items-center justify-end gap-1.5">
                {people.length === 0 && <span className={`text-sm ${role === 'pm' ? 'font-medium text-amber-700 dark:text-amber-300' : 'text-neutral-400'}`}>{t('nobody')}</span>}
                {people.map((m) => (
                  <span key={m.userId} className="inline-flex items-center gap-1 rounded-full bg-neutral-100 py-0.5 pl-2.5 pr-1 text-sm dark:bg-neutral-800">
                    {m.name ?? '—'}
                    {data.me.canManage && <button type="button" aria-label={t('remove', { name: m.name ?? '' })} onClick={() => remove.mutate({ role, userId: m.userId })}
                      className="grid size-6 place-items-center rounded-full text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700 dark:hover:bg-neutral-700">✕</button>}
                  </span>
                ))}
                {data.me.canManage && <button type="button" onClick={() => onAdd(role)} className="min-h-8 rounded-lg px-2 text-xs font-semibold text-brand-700 hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-brand-500/10">+ {t('add')}</button>}
              </dd>
            </div>
          )
        })}
      </dl>
      {data.auSiteManagers.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-xs text-neutral-500">{t('auSiteManagers')}</h4>
          <ul className="space-y-0.5 text-sm">
            {data.auSiteManagers.map((b) => <li key={b.name}>{b.name} <span className="text-xs text-neutral-500">· {t('crewDays', { n: b.crewDays, date: fmtDate(b.lastDay) })}</span></li>)}
          </ul>
        </div>
      )}
    </div>
  )
}

function useSaved(projectId: string, onClose: () => void) {
  const qc = useQueryClient()
  return () => {
    for (const k of [['workflow', projectId], ['execution', projectId], ['workflow-inbox'], ['portfolio']]) void qc.invalidateQueries({ queryKey: k })
    onClose()
  }
}
const errorText = (t: WfT, e: unknown) => (e instanceof ApiError ? e.message : t('couldNotSave'))

function TickDialog({ t, projectId, gate, item, onClose }: { t: WfT; projectId: string; gate: State; item: ItemView; onClose: () => void }) {
  const [note, setNote] = useState('')
  const saved = useSaved(projectId, onClose)
  const save = useMutation({ mutationFn: () => api.put(`/api/projects/${projectId}/gates/${gate}/items/${item.key}`, { note: note || null }), onSuccess: saved })
  return (
    <Dialog title={t('confirmTitle', { item: itemLabel(t, gate, item.key) })} onClose={onClose}
      footer={<><GhostButton onClick={onClose}>{t('cancel')}</GhostButton><PrimaryButton onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? t('saving') : t('confirm')}</PrimaryButton></>}>
      <Field label={t('noteOptional')}>
        <input id="wf-tick-note" className={INPUT} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('notePlaceholder')} maxLength={1000} />
      </Field>
      {save.error && <p role="alert" className="text-sm" style={{ color: 'var(--viz-critical)' }}>{errorText(t, save.error)}</p>}
    </Dialog>
  )
}

/** Signs the next gate (<paramref name="state"/>), or records one of the other states the user may record. */
function SignDialog({ t, projectId, state, choices, open, onClose }:
  { t: WfT; projectId: string; state?: State; choices?: State[]; open: ItemView[]; onClose: () => void }) {
  const te = useExecT()
  const [picked, setPicked] = useState<State>(state ?? choices![0]!)
  const [at, setAt] = useState(todayIso())
  const [note, setNote] = useState('')
  const [reason, setReason] = useState('')
  const saved = useSaved(projectId, onClose)
  const ending = picked === 'lost' || picked === 'cancelled'
  const save = useMutation({
    mutationFn: () => api.post(`/api/projects/${projectId}/sign`, { state: picked, at, note: note || null, overrideReason: reason || null }),
    onSuccess: saved,
  })
  return (
    <Dialog title={state ? t('signTitle', { gate: gateLabel(t, state) }) : t('recordTitle')} hint={t('signHint', { state: stateLabel(te, picked) })} onClose={onClose}
      footer={<><GhostButton onClick={onClose}>{t('cancel')}</GhostButton><PrimaryButton onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? t('saving') : t('sign')}</PrimaryButton></>}>
      {choices && (
        <Field label={t('state')}>
          <Select id="wf-state" value={picked} onChange={(v) => setPicked(v as State)}
            options={choices.map((s) => ({ value: s, label: stateLabel(te, s) }))} />
        </Field>
      )}
      <Field label={t('date')}><input id="wf-at" type="date" className={INPUT} value={at} max={todayIso()} onChange={(e) => setAt(e.target.value)} /></Field>
      <Field label={ending ? t('endNote') : t('noteOptional')}>
        <input id="wf-note" className={INPUT} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
      </Field>
      {open.length > 0 && (
        <Field label={t('overrideReason')} hint={t('overrideHint', { n: open.length })}>
          <textarea id="wf-override" className={INPUT} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} />
        </Field>
      )}
      {save.error && <p role="alert" className="text-sm" style={{ color: 'var(--viz-critical)' }}>{errorText(t, save.error)}</p>}
    </Dialog>
  )
}

function MemberDialog({ t, projectId, role, taken, onClose }: { t: WfT; projectId: string; role: Role; taken: string[]; onClose: () => void }) {
  const { data: people } = useQuery<Person[]>({ queryKey: ['workflow-people'], queryFn: () => api.get<Person[]>('/api/workflow/people') })
  // People holding the role first; anyone else with a role can still be put on the team.
  const list = (people ?? []).filter((p) => !taken.includes(p.userId)).sort((a, b) => Number(b.roles.includes(role)) - Number(a.roles.includes(role)))
  const [userId, setUserId] = useState('')
  const saved = useSaved(projectId, onClose)
  const save = useMutation({ mutationFn: () => api.post(`/api/projects/${projectId}/members`, { role, userId }), onSuccess: saved })
  return (
    <Dialog title={`${t('add')}: ${roleLabel(t, role)}`} onClose={onClose}
      footer={<><GhostButton onClick={onClose}>{t('cancel')}</GhostButton><PrimaryButton onClick={() => save.mutate()} disabled={!userId || save.isPending}>{save.isPending ? t('saving') : t('add')}</PrimaryButton></>}>
      {people && list.length === 0 ? <p className="text-sm text-neutral-500">{t('noPeople')}</p> : (
        <Field label={t('pickPerson')}>
          <Select id="wf-person" value={userId} onChange={setUserId} empty={{ value: '', label: '—' }}
            options={list.map((p) => ({ value: p.userId, label: `${p.name ?? '—'} · ${p.roles.map((r) => roleLabel(t, r)).join(', ')}` }))} />
        </Field>
      )}
      {save.error && <p role="alert" className="text-sm" style={{ color: 'var(--viz-critical)' }}>{errorText(t, save.error)}</p>}
    </Dialog>
  )
}
